"use client";

import * as React from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    ChevronDownIcon,
    XIcon,
    CheckIcon,
    SearchIcon,
    InfoIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import {
    Combobox as ComboboxShadCn,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
} from "./ui/combobox";
import { ImageLoader } from "../ImageLoader";
import { Tooltip } from "../Tooltip";
import { RWA_COIN_ICON } from "~/consts";

export interface ComboboxOption<TValue extends string | number = string> {
    value: TValue;
    label: string;
    icon_url?: string;
    description?: string;
}

/**
 * A labeled group of options. When passed via `optionGroups`, the combobox
 * renders each group as an optgroup-style section with a non-selectable header
 * above its options.
 */
export interface ComboboxOptionGroup<TValue extends string | number = string> {
    label: string;
    options: ComboboxOption<TValue>[];
}

type ComboboxSize = "sm" | "md" | "lg";

export type ComboboxSearchFields = "label" | "all";

interface ComboboxBaseProps<TValue extends string | number = string> {
    value: TValue | null;
    onChange: (value: TValue | null) => void;
    disabled?: boolean;
    showClear?: boolean;
    size?: ComboboxSize;
    /**
     * Size of the items rendered in the dropdown list. Defaults to `size` when
     * not provided, so the list items match the trigger unless overridden.
     */
    itemSize?: ComboboxSize;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    popoverContentClassName?: string;
    /**
     * Which option fields the search matches against. `"label"` (default)
     * matches labels only; `"all"` also matches descriptions, ranking label
     * matches above description-only matches.
     */
    searchFields?: ComboboxSearchFields;
}

/**
 * Pass either a flat list of `options` or grouped `optionGroups` (optgroup
 * style) — exactly one is required. Grouped lists are not virtualized.
 */
type ComboboxProps<TValue extends string | number = string> =
    ComboboxBaseProps<TValue> &
        (
            | { options: ComboboxOption<TValue>[]; optionGroups?: never }
            | { optionGroups: ComboboxOptionGroup<TValue>[]; options?: never }
        );

// Virtualize the list once it grows beyond this many items.
const VIRTUALIZE_THRESHOLD = 50;

// Per-size styling tokens. `itemHeight` is also used as the virtualizer's
// estimated row size so virtualization measures correctly at every size.
const SIZE_CONFIG: Record<
    ComboboxSize,
    {
        trigger: string;
        triggerText: string;
        icon: string;
        item: string;
        itemText: string;
        descriptionText: string;
        itemHeight: number;
        itemHeightWithDescription: number;
        searchInput: string;
    }
> = {
    sm: {
        trigger: "h-8 px-2.5 text-xs",
        triggerText: "text-xs",
        icon: "size-3.5",
        item: "py-1 pl-2.5 pr-8",
        itemText: "text-xs",
        descriptionText: "text-[0.6875rem]",
        itemHeight: 28,
        itemHeightWithDescription: 44,
        // Search input is intentionally a touch taller than list rows.
        searchInput: "h-8 text-xs",
    },
    md: {
        trigger: "h-9 px-3 text-sm",
        triggerText: "text-sm",
        icon: "size-4",
        item: "py-1 pl-2.5 pr-9",
        itemText: "text-sm",
        descriptionText: "text-xs",
        itemHeight: 32,
        itemHeightWithDescription: 50,
        // Search input is intentionally a touch taller than list rows.
        searchInput: "h-9 text-sm",
    },
    lg: {
        trigger: "h-10 px-3.5 text-base",
        triggerText: "text-base",
        icon: "size-5",
        item: "py-2 pl-3 pr-10",
        itemText: "text-base",
        descriptionText: "text-sm",
        itemHeight: 40,
        itemHeightWithDescription: 60,
        // Search input is intentionally a touch taller than list rows.
        searchInput: "h-11 text-base",
    },
};

type Virtualizer = ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>;

type ComboboxFilter = ReturnType<typeof ComboboxPrimitive.useFilter>;

// Matches the query against the configured search fields, ranking options
// whose label matches ahead of options that only match via their description.
function rankOptions<TValue extends string | number>(
    options: ComboboxOption<TValue>[],
    query: string,
    filter: ComboboxFilter,
    searchFields: ComboboxSearchFields,
): ComboboxOption<TValue>[] {
    const labelMatches: ComboboxOption<TValue>[] = [];
    const descriptionMatches: ComboboxOption<TValue>[] = [];
    for (const option of options) {
        if (filter.contains(option.label, query)) {
            labelMatches.push(option);
        } else if (
            searchFields === "all" &&
            option.description &&
            filter.contains(option.description, query)
        ) {
            descriptionMatches.push(option);
        }
    }
    return labelMatches.concat(descriptionMatches);
}

export function Combobox<TValue extends string | number = string>({
    options,
    optionGroups,
    value,
    onChange,
    disabled = false,
    showClear = false,
    size = "md",
    itemSize,
    placeholder = "Select a option...",
    searchPlaceholder = "Search...",
    emptyMessage = "No options found.",
    popoverContentClassName,
    searchFields = "label",
}: ComboboxProps<TValue>) {
    const isGrouped = optionGroups !== undefined;

    // Flattened view of every option, used for selection lookup and to decide
    // whether to render icon slots / descriptions across both layout modes.
    const allOptions = React.useMemo(
        () =>
            optionGroups
                ? optionGroups.flatMap((group) => group.options)
                : (options ?? []),
        [optionGroups, options],
    );

    // base-ui accepts either a flat array or an array of `{ ...meta, items }`
    // groups; map our groups into that shape so its internal filtering works.
    const items = React.useMemo(
        () =>
            optionGroups
                ? optionGroups.map((group) => ({
                      label: group.label,
                      items: group.options,
                  }))
                : (options ?? []),
        [optionGroups, options],
    );

    const selectedOption = allOptions.find((o) => o.value === value) ?? null;
    // Grouped lists render their own per-group sections, so virtualization (a
    // flat windowed list) only applies to the ungrouped path.
    const isVirtualized =
        !isGrouped && allOptions.length > VIRTUALIZE_THRESHOLD;
    const sizeConfig = SIZE_CONFIG[size];
    // Items in the list use their own size, falling back to the trigger size.
    const itemSizeConfig = SIZE_CONFIG[itemSize ?? size];
    // The lg info icon reads as oversized in the trigger, so cap it at the md size.
    const infoIconSize = size === "lg" ? SIZE_CONFIG.md.icon : sizeConfig.icon;
    const hasDescriptions = React.useMemo(
        () => allOptions.some((o) => o.description),
        [allOptions],
    );
    // When any option has an icon, render an icon slot for every row (falling
    // back to the RWA coin icon) so rows stay aligned.
    const iconsPresent = React.useMemo(
        () => allOptions.some((o) => o.icon_url !== undefined),
        [allOptions],
    );

    const [open, setOpen] = React.useState(false);
    const virtualizerRef = React.useRef<Virtualizer | null>(null);

    const [query, setQuery] = React.useState("");
    // Locale-aware, case-insensitive `contains` matcher from base-ui.
    const filter = ComboboxPrimitive.useFilter();

    // Filter externally (via `filteredItems`) rather than with base-ui's
    // internal label-only filtering, so `searchFields="all"` can match
    // descriptions too. See rankOptions for the label-over-description
    // ordering.
    const filteredItems = React.useMemo(() => {
        if (!query) {
            return items;
        }
        if (optionGroups) {
            return optionGroups
                .map((group) => ({
                    label: group.label,
                    items: rankOptions(
                        group.options,
                        query,
                        filter,
                        searchFields,
                    ),
                }))
                .filter((group) => group.items.length > 0);
        }
        return rankOptions(options ?? [], query, filter, searchFields);
    }, [query, items, optionGroups, options, filter, searchFields]);

    return (
        <ComboboxShadCn
            items={items}
            filteredItems={filteredItems}
            inputValue={query}
            onInputValueChange={setQuery}
            value={selectedOption}
            onValueChange={(item) =>
                onChange(item ? (item as ComboboxOption<TValue>).value : null)
            }
            itemToStringLabel={(item) =>
                (item as ComboboxOption<TValue>)?.label ?? ""
            }
            disabled={disabled}
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                // base-ui doesn't reset a controlled input value, so start
                // each open with a fresh (unfiltered) list. Resetting on open
                // rather than close keeps the list stable during the close
                // animation.
                if (nextOpen) {
                    setQuery("");
                }
            }}
            virtualized={isVirtualized}
            onItemHighlighted={
                isVirtualized
                    ? (_item, { reason, index }) => {
                          const virtualizer = virtualizerRef.current;
                          if (index < 0 || !virtualizer) {
                              return;
                          }
                          const isStart = index === 0;
                          const isEnd = index === virtualizer.options.count - 1;
                          const shouldScroll =
                              reason === "none" ||
                              (reason === "keyboard" && (isStart || isEnd));
                          if (shouldScroll) {
                              queueMicrotask(() => {
                                  virtualizer.scrollToIndex(index, {
                                      align: isEnd ? "start" : "end",
                                  });
                              });
                          }
                      }
                    : undefined
            }
        >
            {/* Trigger button — styled like a Select */}
            <div
                className={cn(
                    "relative flex w-full items-center",
                    sizeConfig.trigger.split(" ")[0],
                )}
            >
                <ComboboxPrimitive.Trigger
                    disabled={disabled}
                    className={cn(
                        "flex w-full items-center justify-between gap-2 rounded border border-input bg-background py-2 transition-colors",
                        sizeConfig.trigger,
                        "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "data-popup-open:border-ring data-popup-open:ring-2 data-popup-open:ring-ring/50",
                    )}
                >
                    <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
                        {selectedOption ? (
                            <>
                                {iconsPresent && (
                                    <ImageLoader
                                        src={selectedOption.icon_url ?? null}
                                        alt=""
                                        aria-hidden
                                        fallbackSrc={RWA_COIN_ICON}
                                        fallbackClassName="bg-white opacity-50"
                                        className={cn(
                                            "rounded-full shrink-0 object-contain",
                                            sizeConfig.icon,
                                        )}
                                    />
                                )}
                                <span className="truncate text-foreground">
                                    {selectedOption.label}
                                </span>
                                {selectedOption.description && (
                                    <Tooltip
                                        placement="right"
                                        tooltipContent={
                                            <div className="max-w-xs px-3 py-2 text-sm">
                                                {selectedOption.description}
                                            </div>
                                        }
                                    >
                                        {({ setTriggerRef }) => (
                                            <span
                                                ref={setTriggerRef}
                                                className="flex shrink-0 items-center text-muted-foreground transition-colors hover:text-foreground"
                                                // Don't toggle the popup when
                                                // interacting with the info icon.
                                                onPointerDown={(e) =>
                                                    e.stopPropagation()
                                                }
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <InfoIcon
                                                    aria-hidden
                                                    className={cn(
                                                        "cursor-help",
                                                        infoIconSize,
                                                    )}
                                                />
                                            </span>
                                        )}
                                    </Tooltip>
                                )}
                            </>
                        ) : (
                            <span className="text-muted-foreground">
                                {placeholder}
                            </span>
                        )}
                    </span>
                    <ChevronDownIcon
                        className={cn(
                            "shrink-0 text-muted-foreground",
                            sizeConfig.icon,
                        )}
                    />
                </ComboboxPrimitive.Trigger>

                {/* Clear button — only shown when showClear is true and a value is selected */}
                {showClear && value && !disabled && (
                    <ComboboxPrimitive.Clear
                        className="absolute right-7 flex size-5 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label="Clear selection"
                        onClick={() => onChange(null)}
                    >
                        <XIcon className="size-3.5" />
                    </ComboboxPrimitive.Clear>
                )}
            </div>

            {/* Popup — search input lives inside here */}
            <ComboboxContent className={popoverContentClassName}>
                {/* Search input rendered inside the popup, with a leading magnifying
                glass icon and a bottom border separating it from the list */}
                <div className="relative flex items-center border-b border-border">
                    <SearchIcon
                        aria-hidden
                        className={cn(
                            "pointer-events-none absolute left-2 shrink-0 text-muted-foreground",
                            sizeConfig.icon,
                        )}
                    />
                    <ComboboxPrimitive.Input
                        placeholder={searchPlaceholder}
                        className={cn(
                            "w-full border-0 bg-transparent pl-9 pr-2 outline-none placeholder:text-muted-foreground",
                            // @tailwindcss/forms applies a blue ring + border to
                            // focused inputs; neutralize it so the divider below the
                            // search field doesn't appear to turn blue on focus.
                            "focus:border-transparent focus:shadow-none focus:ring-0 focus:outline-none",
                            sizeConfig.searchInput,
                        )}
                    />
                </div>
                <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>

                {isVirtualized ? (
                    // The VirtualizedList renders its own scroll viewport (required
                    // by react-virtual), so neutralize ComboboxList's own scrolling
                    // here — otherwise the two nested scroll containers each show a
                    // scrollbar. `max-h-none!` is important because twMerge can't
                    // dedupe ComboboxList's comma-containing arbitrary max-h value.
                    <ComboboxList className="max-h-none! overflow-y-visible p-0">
                        <VirtualizedList
                            open={open}
                            virtualizerRef={virtualizerRef}
                            sizeConfig={itemSizeConfig}
                            hasDescriptions={hasDescriptions}
                            iconsPresent={iconsPresent}
                        />
                    </ComboboxList>
                ) : isGrouped ? (
                    <ComboboxList className="p-0">
                        {(group: {
                            label: string;
                            items: ComboboxOption<TValue>[];
                        }) => (
                            <ComboboxGroup
                                key={group.label}
                                items={group.items}
                            >
                                <ComboboxLabel className="font-light uppercase tracking-wide pt-2 pb-0">
                                    {group.label}
                                </ComboboxLabel>
                                <ComboboxCollection>
                                    {(item) => {
                                        const option =
                                            item as ComboboxOption<TValue>;
                                        return (
                                            <ComboboxOptionRow
                                                key={option.value}
                                                option={option}
                                                iconsPresent={iconsPresent}
                                                sizeConfig={itemSizeConfig}
                                                className="pl-5"
                                            />
                                        );
                                    }}
                                </ComboboxCollection>
                            </ComboboxGroup>
                        )}
                    </ComboboxList>
                ) : (
                    <ComboboxList className="p-0">
                        {(item) => {
                            const option = item as ComboboxOption<TValue>;
                            return (
                                <ComboboxOptionRow
                                    key={option.value}
                                    option={option}
                                    iconsPresent={iconsPresent}
                                    sizeConfig={itemSizeConfig}
                                />
                            );
                        }}
                    </ComboboxList>
                )}
            </ComboboxContent>
        </ComboboxShadCn>
    );
}

// A single selectable row in the (non-virtualized) dropdown. Shared between the
// flat and grouped list layouts so both render rows identically.
function ComboboxOptionRow<TValue extends string | number = string>({
    option,
    iconsPresent,
    sizeConfig,
    className,
}: {
    option: ComboboxOption<TValue>;
    iconsPresent: boolean;
    sizeConfig: (typeof SIZE_CONFIG)[ComboboxSize];
    className?: string;
}) {
    return (
        <ComboboxItem
            value={option}
            className={cn(
                "rounded-none",
                className,
                sizeConfig.itemText,
                // Stack the icon+label row above the description so the
                // description aligns with the icon rather than the label.
                option.description && "flex-col items-start gap-0",
            )}
        >
            <span className="flex w-full min-w-0 items-center gap-2">
                {iconsPresent && (
                    <ImageLoader
                        src={option.icon_url ?? null}
                        alt=""
                        fallbackSrc={RWA_COIN_ICON}
                        aria-hidden
                        fallbackClassName="bg-white opacity-50"
                        className={cn(
                            "shrink-0 rounded-full object-contain",
                            sizeConfig.icon,
                        )}
                    />
                )}
                <span className="truncate">{option.label}</span>
            </span>
            {option.description && (
                <span
                    className={cn(
                        "w-full truncate text-muted-foreground",
                        sizeConfig.descriptionText,
                    )}
                >
                    {option.description}
                </span>
            )}
        </ComboboxItem>
    );
}

// Virtualized list body — rendered inside Combobox.List when the option count
// exceeds VIRTUALIZE_THRESHOLD. Uses @tanstack/react-virtual to only render the
// items currently visible in the scroll viewport.
function VirtualizedList({
    open,
    virtualizerRef,
    sizeConfig,
    hasDescriptions,
    iconsPresent,
}: {
    open: boolean;
    virtualizerRef: React.RefObject<Virtualizer | null>;
    sizeConfig: (typeof SIZE_CONFIG)[ComboboxSize];
    hasDescriptions: boolean;
    iconsPresent: boolean;
}) {
    const filteredItems = ComboboxPrimitive.useFilteredItems<ComboboxOption>();
    const scrollElementRef = React.useRef<HTMLDivElement | null>(null);
    const rowHeight = hasDescriptions
        ? sizeConfig.itemHeightWithDescription
        : sizeConfig.itemHeight;

    const virtualizer = useVirtualizer({
        enabled: open,
        count: filteredItems.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: () => rowHeight,
        overscan: 20,
        paddingStart: 4,
        paddingEnd: 4,
        scrollPaddingStart: 4,
        scrollPaddingEnd: 4,
    });

    React.useImperativeHandle(virtualizerRef, () => virtualizer);

    const handleScrollElementRef = React.useCallback(
        (element: HTMLDivElement | null) => {
            scrollElementRef.current = element;
            if (element) {
                virtualizer.measure();
            }
        },
        [virtualizer],
    );

    const totalSize = virtualizer.getTotalSize();

    if (!filteredItems.length) {
        return null;
    }

    return (
        <div
            role="presentation"
            ref={handleScrollElementRef}
            className="hide-scrollbar h-[min(21rem,var(--total-size))] max-h-[calc(var(--available-height)-(--spacing(9)))] overflow-y-auto overscroll-contain scroll-py-1"
            style={{ "--total-size": `${totalSize}px` } as React.CSSProperties}
        >
            <div
                role="presentation"
                className="relative w-full"
                style={{ height: totalSize }}
            >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                    const option = filteredItems[virtualItem.index];
                    if (!option) {
                        return null;
                    }
                    return (
                        <ComboboxPrimitive.Item
                            key={virtualItem.key}
                            index={virtualItem.index}
                            value={option}
                            aria-setsize={filteredItems.length}
                            aria-posinset={virtualItem.index + 1}
                            className={cn(
                                "absolute top-0 left-0 flex w-full cursor-default outline-hidden select-none",
                                // Stack the icon+label row above the description so
                                // the description aligns with the icon, not the label.
                                option.description
                                    ? "flex-col items-start gap-1"
                                    : "items-center gap-2",
                                sizeConfig.item,
                                sizeConfig.itemText,
                                "data-selected:bg-primary-light data-selected:text-primary data-highlighted:not-data-selected:bg-muted data-highlighted:not-data-selected:text-foreground",
                            )}
                            style={{
                                height: virtualItem.size,
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            <span className="flex w-full min-w-0 items-center gap-2">
                                {iconsPresent && (
                                    <ImageLoader
                                        src={option.icon_url ?? null}
                                        fallbackSrc={RWA_COIN_ICON}
                                        alt=""
                                        aria-hidden
                                        fallbackClassName="bg-white opacity-50"
                                        className={cn(
                                            "shrink-0 rounded-full object-contain",
                                            sizeConfig.icon,
                                        )}
                                    />
                                )}
                                <span className="truncate">{option.label}</span>
                            </span>
                            {option.description && (
                                <span
                                    className={cn(
                                        "flex w-full truncate text-muted-foreground",
                                        sizeConfig.descriptionText,
                                    )}
                                >
                                    {option.description}
                                </span>
                            )}
                            <ComboboxPrimitive.ItemIndicator
                                className={cn(
                                    "absolute right-2 flex items-center justify-center",
                                    sizeConfig.icon,
                                )}
                            >
                                <CheckIcon
                                    className={cn(
                                        sizeConfig.icon,
                                        "text-primary",
                                    )}
                                />
                            </ComboboxPrimitive.ItemIndicator>
                        </ComboboxPrimitive.Item>
                    );
                })}
            </div>
        </div>
    );
}
